/**
 * Entry 좌측 메뉴 그룹
 * SSOT: ITOps Admin(Portal 서비스 관리) services.json 의 첫 번째 비-Service 라벨.
 * 'Service' 는 서비스 폴더 마커이며 메뉴 그룹이 아니다.
 */

const ENTRY_CATEGORY_SORT_KEYS = [
  'itsm',
  'security',
  'cloudops',
  'zerotrust',
  'aicyberops',
  'aiops',
  'customize',
  'system',
  'portal',
] as const;

type KnownCategoryKey = (typeof ENTRY_CATEGORY_SORT_KEYS)[number];

const GENERIC_MENU_LABELS = new Set(['service']);

/** 구 라벨·키와 현재 Admin 라벨을 같은 그룹으로 묶기 위한 별칭 (menu-config 키 유지) */
const CATEGORY_ALIASES: Record<string, KnownCategoryKey> = {
  '차세대 itsm': 'itsm',
  camp: 'itsm',
  itsm: 'itsm',
  'asset management': 'itsm',
  assetmanagement: 'itsm',
  security: 'security',
  'cyber security': 'security',
  cybersecurity: 'security',
  cloudops: 'cloudops',
  'zero trust': 'zerotrust',
  zerotrust: 'zerotrust',
  'ai cyberops': 'aicyberops',
  aicyberops: 'aicyberops',
  'ai 사이berops': 'aicyberops',
  aiops: 'aiops',
  portal: 'portal',
  system: 'system',
  customize: 'customize',
};

const CATEGORY_COLORS: Record<KnownCategoryKey | 'other', string> = {
  itsm: 'hsl(195 85% 38%)',
  security: 'hsl(0 72% 42%)',
  cloudops: 'hsl(210 90% 42%)',
  zerotrust: 'hsl(158 64% 32%)',
  aicyberops: 'hsl(25 95% 45%)',
  aiops: 'hsl(270 70% 48%)',
  portal: 'hsl(245 58% 44%)',
  system: 'hsl(220 9% 46%)',
  customize: 'hsl(32 90% 42%)',
  other: 'hsl(220 9% 46%)',
};

const CUSTOMIZE_SORT_INDEX = ENTRY_CATEGORY_SORT_KEYS.indexOf('customize');

export function isGenericMenuLabel(label: string): boolean {
  return GENERIC_MENU_LABELS.has(label.trim().toLowerCase());
}

/** 메뉴 그룹으로 쓸 대표 라벨 — Service 마커를 건너뛴 첫 라벨 */
export function getPrimaryMenuLabel(labels: string[] | undefined): string | undefined {
  if (!Array.isArray(labels) || labels.length === 0) return undefined;
  const named = labels.find((l) => typeof l === 'string' && l.trim() && !isGenericMenuLabel(l));
  const fallback = labels.find((l) => typeof l === 'string' && l.trim());
  const value = named ?? fallback;
  return value?.trim() || undefined;
}

function slugifyCategory(category: string): string {
  const slug = category
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9가-힣]+/gi, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'other';
}

export function normalizeCategoryKey(category: string): string {
  if (!category || typeof category !== 'string') return 'other';
  const normalized = category.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!normalized) return 'other';
  if (normalized === '차세대 itsm') return 'itsm';
  const compact = normalized.replace(/\s+/g, '');
  return CATEGORY_ALIASES[normalized] ?? CATEGORY_ALIASES[compact] ?? slugifyCategory(category);
}

function getCategorySortIndex(category: string): number {
  const key = normalizeCategoryKey(category);
  const idx = ENTRY_CATEGORY_SORT_KEYS.indexOf(key as KnownCategoryKey);
  if (idx !== -1) return idx;
  // 신규 Admin 라벨은 customize 다음, PORTAL/SYSTEM 앞
  return CUSTOMIZE_SORT_INDEX + 0.5;
}

/** Entry 사이드바 그룹 정렬 */
export function sortEntryCategories(categories: string[]): string[] {
  return [...categories].sort((a, b) => {
    const orderDiff = getCategorySortIndex(a) - getCategorySortIndex(b);
    if (orderDiff !== 0) return orderDiff;
    return a.localeCompare(b, 'ko');
  });
}

/** 정규화 키 기준 동일 그룹 (CloudOps / Cloudops, CAMP / Asset Management 등) */
export function entryCategoriesMatch(a: string, b: string): boolean {
  return normalizeCategoryKey(a) === normalizeCategoryKey(b);
}

/** 동일 그룹 키의 중복 제거. 표시명은 전달된 Admin 라벨(원문)을 유지한다. */
export function dedupeEntryCategories(categories: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const cat of categories) {
    if (!cat || typeof cat !== 'string') continue;
    const trimmed = cat.trim();
    if (!trimmed) continue;
    const key = normalizeCategoryKey(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(trimmed);
  }
  return sortEntryCategories(unique);
}

/** 그룹 헤더 — ITOps Admin 라벨 원문 */
export function getEntryCategoryDisplayLabel(category: string): string {
  return category.trim();
}

export function getEntryCategoryColor(category: string): string {
  const key = normalizeCategoryKey(category);
  if (key in CATEGORY_COLORS) {
    return CATEGORY_COLORS[key as KnownCategoryKey | 'other'];
  }
  let hash = 0;
  for (let i = 0; i < category.length; i += 1) {
    hash = (hash * 31 + category.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 55% 38%)`;
}
